import {FC, useCallback, useEffect, useRef, useState} from "react";
import {OrganizationsList} from "../OrganizationsList/OrganizationsList";
import {AddingOrganization} from "../AddingOrganization/AddingOrganization";
import "./OrganizationsTable.styles.sass";
import {useAppDispatch, useAppSelector} from "toolkit/hooks";
import {ReactComponent as BackArrowIcon} from "images/back-arrow.svg";
import {Button} from "components/shared";
import {OrganizationLegend} from "components/OrganizationLegend/OrganizationLegend";
import {ModalWindow} from "components/ModalWindow/ModalWindow";
import {ReactComponent as PlusIcon} from "images/PlusBold.svg";
import Search from "components-new/Search";
import {ChangeArchiveMode, ChangeSearchValue, ClearSearch} from "toolkit/organizations/organizations.slice";
import useDebounce from "hooks/useDebounce";
import {getOrganizationsSearched} from "toolkit/organizations/organizations.thunks";
import {BUTTON_VARIANTS} from "components/shared/Button/Button";
import Badge from "components-new/Badge";
import {useLocation, useNavigate} from "react-router-dom";
import {userAPI} from "libs/apiClient/User";
import ModalPortal from "../../components-new/ModalPortal";
import {uploaderAPI} from "../../libs/apiClient/Uploader";
import {AxiosError} from "axios";
import {snackWrapper} from "../../helpers";

interface OrganizationsTableProps {
    title: string;
}

export const OrganizationsTable: FC<OrganizationsTableProps> = ({title}) => {
    const [addingOrganization, setAddingOrganization] = useState<boolean>(false);
    const isActive: boolean = true;
    const navigate = useNavigate();
    const location = useLocation();
    const mode = useAppSelector((state) => state.organizations.mode);
    const isAdmin = useAppSelector((state) => state.profile.profile.admin);
    const {searchValue, archiveMode} = useAppSelector((state) => state.organizations);
    const dispatch = useAppDispatch();

    // открыть
    // const { isActive } = useSelector((state: RootState) => state.stateProfile.profile);
    const closeAddingOrganization = () => {
        setAddingOrganization(false);
    };

    const handleSearch = (text: string) => {
        dispatch(ChangeSearchValue(text));
    };

    const query = useDebounce(searchValue, 500);

    useEffect(() => {
        if (query as string) {
            dispatch(
                getOrganizationsSearched({
                    query: query as string,
                    isCategorization: mode === "categorization",
                    archiveMode
                }),
            );
        } else if (query === "") {
        }
        //eslint-disable-next-line
    }, [query]);

    useEffect(() => {
        dispatch(ClearSearch());
        //eslint-disable-next-line
    }, [archiveMode]);

    useEffect(() => {
        dispatch(ChangeArchiveMode(location.pathname.includes("archive")));
        //eslint-disable-next-line
    }, [location.pathname]);

    const [created, setCreated] = useState(false);

    useEffect(() => {
        userAPI.permissionsCheckCreate(0, ["system.organization.create"])
            .then(({data}) => setCreated(!!data.length));
    }, []);

    /* Imported Modal */

    const [updateLoader, setUpdateLoader] = useState(false)

    const [showModal, setShowModal] = useState(false);
    const toggle = () => setShowModal(prevState => !prevState);

    const downloadTemplate = () => {
        uploaderAPI.uploaderGet({format: 'blob'}).then((response) => {
            const name = response.headers['content-disposition']?.replace(/^.+filename=/, '') || 'template.xlsx'
            const link = document.createElement('a');
            const href = URL.createObjectURL(response.data)
            link.href = href
            link.download = decodeURI(name)
            link.click()
            link.remove()
        });
    };

    const inputUpdater = useRef<null | HTMLInputElement>(null)

    const update = useCallback(() => {
        if (inputUpdater.current) {
            if (inputUpdater.current.files && inputUpdater.current.files.length) {
                const file = inputUpdater.current.files[0]
                inputUpdater.current!.value = '';
                setUpdateLoader(true)
				uploaderAPI.uploaderCreate({file})
					.then(() => {
                        toggle()
					})
					.catch((err:AxiosError<string>) => {
                        console.log(err.response?.data);
                        if (err.response?.data) {
                                snackWrapper('error',err.response?.data||'');
                        }
					})
					.finally(() => setUpdateLoader(false));
            }
        }
    }, [inputUpdater.current]);

    /* ============== */
    return (
        <main className="content">
            {showModal &&
                <ModalPortal title={"Импорт данных"} handleClose={toggle}>
                    <div className={'modal-import-data'}>
                        <p>Перед загрузкой данных необходимо убедиться в том, что в системе созданы все содержащиеся в
                            файле сущности организаций и сотрудники.</p>
                        <input onChange={update} type="file"
                               accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                               ref={inputUpdater} hidden/>
                        <Button disabled={updateLoader}
                                onClick={downloadTemplate}
                                variant={BUTTON_VARIANTS.TEXT}>Скачать шаблон</Button>
                        <Button disabled={updateLoader}
                                loading={updateLoader}
                                onClick={() => inputUpdater.current?.click()}>Загрузить</Button>
                    </div>
                </ModalPortal>
            }
            <section className="organizations">
                <div className="organizations-heading">
                    <div className="organizations-heading-item">
                        <div onClick={() => navigate(-1)} className="organizations-heading-item-back">
                            <BackArrowIcon/>
                        </div>
                        <h3 className="organizations-title">{title}</h3>
                        {archiveMode && <Badge color="#7CADAA">Архив</Badge>}
                    </div>
                    <div className="organizations-heading-item">
                        {!archiveMode && mode !== "categorization" && isAdmin && (
                            <Button onClick={toggle} variant={BUTTON_VARIANTS.TEXT}>
                                Импортировать
                            </Button>)}
                        <Search
                            className="organization-search"
                            searchValue={searchValue}
                            handleSearch={(e) => handleSearch(e.target.value)}
                        />
                        {!archiveMode && mode !== "categorization" && (
                            <Button to={"archive"} variant={BUTTON_VARIANTS.OUTLINED} className="btn"
                                    disabled={!isActive}>
                                Перейти в архив
                            </Button>
                        )}
                        {!archiveMode && mode !== "categorization" && (
                            <Button className="btn" onClick={() => setAddingOrganization(true)}
                                    disabled={!isActive || !created}>
                                <div>
                                    <PlusIcon/>
                                </div>
                                Новая организация
                            </Button>
                        )}
                        {addingOrganization && (
                            <ModalWindow close={() => setAddingOrganization(false)}>
                                <AddingOrganization close={closeAddingOrganization}
                                                    title="Добавление карточки организации"/>
                            </ModalWindow>
                        )}
                    </div>
                </div>
                {/* <div className={isActive ? "" : "organizations-table-overlay-wrp"}> */}
                {!isActive && (
                    <span data-comment-false={true} className="organizations-table-overlay-warning">
						Доступ закрыт, запросите подтверждение у администратора
					</span>
                )}
                {/* <div className={isActive ? "" : "organizations-table-overlay"}> */}
                <OrganizationLegend/>
                <table className="organizations-table">
                    <thead className="organizations-table-head">
                    <tr className="organizations-table-head-row">
                        <th className="organizations-table-head-row-item">Тип</th>
                        <th className="organizations-table-head-row-item">Название организации</th>
                        <th className="organizations-table-head-row-item">ИНН</th>
                        <th className="organizations-table-head-row-item">КПП</th>
                        <th className="organizations-table-head-row-item">ОГРН</th>
                        <th className="organizations-table-head-row-item"></th>
                        <th className="organizations-table-head-row-item">Маркеры</th>
                        {/* <th className="organizations-table-head-row-item">Статус категорирования</th> */}
                        {/* <th className="organizations-table-head-row-item">Наличие КИИ</th> */}
                        {/* <th className="organizations-table-head-row-item"></th> */}
                    </tr>
                    </thead>
                    <OrganizationsList/>
                </table>
                {/* </div> */}
                {/* </div> */}
            </section>
        </main>
    );
};
